import { useCallback, useEffect, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import {
  deleteFromListedResponseCache,
  setListedResponseCache,
  upsertListToListedResponseCache,
  upsertToListedResponseCache,
} from 'shared/functions-ts';

import type { Identifier, XYCoord } from 'dnd-core';
import type {
  DnDItem,
  GroupRowState,
  RearrangeHandler,
  RearrangeHandlerParams,
  RowState,
  UseCrudParams,
  UseDefaultActionsParams,
  UseDndParams,
  UseSorterParams,
} from './types';

import { dnDTypes } from './constants';
import { TableState } from './types';

export const useReactQueryCruds = <E>({
  create,
  update,
  remove,
  fetchOne,
  fetchList,
  entityIdKey,
  entityCacheName,
}: UseCrudParams<E>) => {
  const queryClient = useQueryClient();

  const queryForList = useQuery(entityCacheName, fetchList);

  const queryForOne = useQuery(entityCacheName, fetchOne);

  const setCache = useCallback(
    (items: E[]) => {
      const listedResponse = {
        items,
        total: items?.length,
      };
      setListedResponseCache(queryClient, entityCacheName, listedResponse);
    },
    [queryClient, entityCacheName],
  );

  const upsertIntoCache = useCallback(
    (entry: Partial<E>) => {
      upsertToListedResponseCache(queryClient, entityCacheName, entityIdKey, queryForList.data, entry);
    },
    [queryClient, entityCacheName, entityIdKey, queryForList.data],
  );

  const upsertListIntoCache = useCallback(
    (entries: Partial<E>[]) => {
      upsertListToListedResponseCache(queryClient, entityCacheName, entityIdKey, queryForList.data, entries);
    },
    [queryClient, entityCacheName, entityIdKey, queryForList.data],
  );

  const removeFromCache = useCallback(
    (entry: Partial<E>) => {
      deleteFromListedResponseCache(queryClient, entityCacheName, entityIdKey, queryForList.data, entry);
    },
    [queryClient, entityCacheName, entityIdKey, queryForList.data],
  );

  const mutationForCreate = useMutation(create, {
    onSuccess: upsertIntoCache,
  });

  const mutationForUpdate = useMutation(update, {
    onSuccess: upsertIntoCache,
  });

  const mutationForRemove = useMutation(remove, {
    onSuccess: (_, deletedEntry) => {
      removeFromCache(deletedEntry);
    },
  });

  return {
    queryClient,
    queryForOne,
    queryForList,
    mutationForCreate,
    mutationForUpdate,
    mutationForRemove,
    setCache,
    upsertIntoCache,
    removeFromCache,
    upsertListIntoCache,
  } as const;
};

export const useReactQueryDefaultActions = <E>(params: UseDefaultActionsParams<E>) => {
  const { share, enable, disable, ...baseParams } = params;

  const cruds = useReactQueryCruds(baseParams);

  const mutationForShare = useMutation(share, {
    onSuccess: (entry) => {
      upsertToListedResponseCache(
        cruds.queryClient,
        params.entityCacheName,
        params.entityIdKey,
        cruds.queryForList.data,
        entry,
      );
    },
  });

  const mutationForEnable = useMutation(enable, {
    onSuccess: (entry) => {
      upsertToListedResponseCache(
        cruds.queryClient,
        params.entityCacheName,
        params.entityIdKey,
        cruds.queryForList.data,
        {
          ...entry,
          disabled: false,
        },
      );
    },
  });

  const mutationForDisable = useMutation(disable, {
    onSuccess: (entry) => {
      upsertToListedResponseCache(
        cruds.queryClient,
        params.entityCacheName,
        params.entityIdKey,
        cruds.queryForList.data,
        {
          ...entry,
          disabled: true,
        },
      );
    },
  });

  return {
    ...cruds,
    mutationForShare,
    mutationForEnable,
    mutationForDisable,
  };
};

export const useSorter = <E>({
  data,
  entityIdKey,
  entityGroupIdKey,
  entityCacheName,
  updateAndSort,
}: UseSorterParams<E>) => {
  const queryClient = useQueryClient();

  const mutationForSort = useMutation(updateAndSort, {
    onSuccess: (entries) => {
      upsertListToListedResponseCache(queryClient, entityCacheName, entityIdKey, data, entries.items);
    },
  });

  const onApplySort = useCallback(
    async ({ itemId, prevGroupId, nextGroupId, prevGroupOrder, nextGroupOrder }: RearrangeHandlerParams) => {
      if (prevGroupId === nextGroupId) {
        await mutationForSort.mutateAsync({
          groupItemIds: [nextGroupOrder],
        });
        return;
      }

      const itemForUpdate = data.items.find((item) => item[entityIdKey] === itemId);

      const entityToUpdate = {
        ...itemForUpdate,
        [entityGroupIdKey]: nextGroupId || null,
      };

      await mutationForSort.mutateAsync({
        entityToUpdate,
        groupItemIds: [prevGroupOrder, nextGroupOrder],
      });
    },
    [entityIdKey, entityGroupIdKey, data.items, mutationForSort],
  );

  return {
    onApplySort,
    mutationForSort,
  } as const;
};

export const useDndList = <I>(items: I[], groupId: number, onRearrange: RearrangeHandler) => {
  const [{ handlerId, isOver }, drop] = useDrop({
    accept: dnDTypes.groupedListItem,
    drop: (item: DnDItem<I>) => {
      onRearrange({
        itemId: item.itemId,
        prevGroupId: item.groupId,
        nextGroupId: groupId,
        prevIndex: item.prevIndex,
        nextIndex: item.index,
      });
    },
    hover(item: DnDItem<I>) {
      if (items?.length > 0) {
        return;
      }

      onRearrange({
        itemId: item.itemId,
        prevGroupId: item.groupId,
        nextGroupId: groupId,
        prevIndex: item.index,
        nextIndex: 0,
        isDragging: true,
      });
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
      handlerId: monitor.getHandlerId(),
    }),
  });

  return {
    isOver,
    drop,
    handlerId,
  } as const;
};

export const useDndItem = <P>({ ref, index, itemId, groupId, payload, disabled, onRearrange }: UseDndParams<P>) => {
  const [{ handlerId }, drop] = useDrop<DnDItem<P>, void, { handlerId: Identifier | null }>({
    accept: dnDTypes.groupedListItem,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DnDItem<P>, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      onRearrange({
        itemId: item.itemId,
        prevGroupId: item.groupId,
        nextGroupId: groupId,
        prevIndex: dragIndex,
        nextIndex: hoverIndex,
        isDragging: true,
      });

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex; // <-- set new index here
    },
  });

  const [{ isDragging, item: draggingItem }, drag] = useDrag({
    type: dnDTypes.groupedListItem,
    item: () => ({
      index,
      itemId,
      groupId,
      prevIndex: index,
      payload,
    }),
    canDrag: () => !disabled,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      item: monitor.getItem(),
    }),
  });

  return {
    drag,
    drop,
    handlerId,
    isDragging,
    draggingItem,
  } as const;
};

export const useTableState = () => {
  const [tableState, setTableState] = useState<TableState>({});

  const updateTableState = useCallback((updates: Partial<TableState>) => {
    setTableState((currentState) => {
      const nextState = {
        ...currentState,
        ...updates,
      };

      return nextState;
    });
  }, []);

  return {
    tableState,
    setTableState,
    updateTableState,
  };
};

export const useRowStates = <E, RS extends RowState>(
  entities: E[],
  getEntityId: (entity: E) => number,
  getDefaultState: (entity: E) => RS,
) => {
  const [rowStates, setRowStates] = useState<{
    [key: number]: GroupRowState;
  }>(() =>
    entities.reduce((acc, entity) => {
      acc[getEntityId(entity)] = getDefaultState(entity);
      return acc;
    }, {}),
  );

  useEffect(() => {
    const entitiesWithoutState = entities.filter((group) => rowStates[getEntityId(group)] == null);

    const stateWithoutEntityIds = Object.keys(rowStates).filter((entityId) => {
      const existingEntity = entities.find((g) => {
        const id = getEntityId(g)?.toString() || 'undefined';
        return id === entityId;
      });
      return !existingEntity;
    });

    if (stateWithoutEntityIds.length <= 0 && entitiesWithoutState.length <= 0) {
      return;
    }

    let nextStates = stateWithoutEntityIds.reduce((acc, groupId) => {
      delete acc[groupId];
      return acc;
    }, rowStates);

    nextStates = entitiesWithoutState.reduce((acc, group) => {
      acc[getEntityId(group)] = getDefaultState(group);
      return acc;
    }, nextStates);

    setRowStates(nextStates);
  }, [entities, rowStates, getEntityId, getDefaultState]);

  const updateRowState = useCallback(
    (entity: E, updates: Partial<RS>, parentId?: number) => {
      setRowStates((currentStates) => {
        const entityId = getEntityId(entity);
        const currentState = currentStates[entityId];

        const nextState = {
          ...currentState,
          ...updates,
        };

        const nextStates = {
          ...currentStates,
          [entityId]: nextState,
        };

        return nextStates;
      });
    },
    [getEntityId],
  );

  const addEntityState = useCallback(
    (entity: E) => {
      setRowStates((currentStates) => {
        const entityId = getEntityId(entity);

        const nextStates = {
          ...currentStates,
          [entityId]: getDefaultState(entity),
        };

        return nextStates;
      });
    },
    [getEntityId, getDefaultState],
  );

  const removeEntityState = useCallback(
    (entity: E) => {
      setRowStates((currentStates) => {
        const entityId = getEntityId(entity);

        const nextStates = {
          ...currentStates,
        };

        delete nextStates[entityId];

        return nextStates;
      });
    },
    [getEntityId],
  );

  return {
    rowStates,
    setRowStates,
    addEntityState,
    updateRowState,
    removeEntityState,
  } as const;
};

export const useGroupRowStates = <E>(
  groups: E[],
  getGroupId: (group: E) => number,
  getDefaultState: (group: E) => Partial<GroupRowState>,
) => {
  const { rowStates, setRowStates, addEntityState, updateRowState, removeEntityState } = useRowStates(
    groups,
    getGroupId,
    getDefaultState,
  );

  const collapse = useCallback(
    (group) => {
      updateRowState(group, { isExpanded: false });
    },
    [updateRowState],
  );

  const expand = useCallback(
    (group) => {
      updateRowState(group, { isExpanded: true });
    },
    [updateRowState],
  );

  const collapseAll = useCallback(() => {
    const nextStates = Object.entries(rowStates).reduce((acc, [groupId, currentGroupState]) => {
      acc[groupId] = {
        ...currentGroupState,
        isExpanded: false,
      };
      return acc;
    }, {});
    setRowStates(nextStates);
  }, [rowStates, setRowStates]);

  const expandAll = useCallback(() => {
    const nextStates = Object.entries(rowStates).reduce((acc, [groupId, currentGroupState]) => {
      acc[groupId] = {
        ...currentGroupState,
        isExpanded: true,
      };
      return acc;
    }, {});
    setRowStates(nextStates);
  }, [rowStates, setRowStates]);

  return {
    groupStates: rowStates,
    expand,
    collapse,
    expandAll,
    collapseAll,
    addGroupState: addEntityState,
    updateGroupState: updateRowState,
    removeGroupState: removeEntityState,
  } as const;
};
